import React, { useCallback, useState } from "react";
import type { UploadProps } from "antd";
import { Button, Card, Spin, Upload, message } from "antd";
import type { RcFile } from "antd/es/upload";

interface ImageUploadProps {
  value?: string;
  onChange?: (value?: string) => void;
  maxSize?: number;
  disabled?: boolean;
  altText?: string;
  id?: string;
}

const { Dragger } = Upload;
const ACCEPTED_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
];

export const ImageUpload: React.FC<ImageUploadProps> = ({
  value,
  onChange,
  maxSize = 3,
  disabled = false,
  altText = "Превью изображения кампании",
  id,
}) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [uploading, setUploading] = useState(false);

  const readFileAsDataUrl = useCallback((file: RcFile) => {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (event) => reject(event);
      reader.readAsDataURL(file);
    });
  }, []);

  const handleBeforeUpload = useCallback<Required<UploadProps>["beforeUpload"]>(
    async (file) => {
      if (disabled) {
        return Upload.LIST_IGNORE;
      }

      if (!ACCEPTED_TYPES.includes(file.type)) {
        messageApi.error("Разрешены только JPEG, PNG, GIF или WebP изображения");
        return Upload.LIST_IGNORE;
      }

      const sizeInMb = file.size / 1024 / 1024;
      if (sizeInMb > maxSize) {
        messageApi.error(`Размер файла не должен превышать ${maxSize} МБ`);
        return Upload.LIST_IGNORE;
      }

      const shouldShowProgress = sizeInMb > 1;
      if (shouldShowProgress) {
        setUploading(true);
      }

      try {
        const dataUrl = await readFileAsDataUrl(file as RcFile);
        onChange?.(dataUrl);
      } catch (error) {
        void error;
        messageApi.error("Не удалось загрузить изображение");
      } finally {
        if (shouldShowProgress) {
          setUploading(false);
        }
      }

      return Upload.LIST_IGNORE;
    },
    [maxSize, messageApi, onChange, readFileAsDataUrl],
  );

  const handleRemoveClick = () => {
    if (!disabled) {
      onChange?.(undefined);
    }
  };

  return (
    <div>
      {contextHolder}
      <Dragger
        multiple={false}
        showUploadList={false}
        beforeUpload={handleBeforeUpload}
        accept={ACCEPTED_TYPES.join(",")}
        disabled={disabled}
        id={id}
      >
        <p className="ant-upload-text">
          Перетащите изображение или нажмите для выбора файла
        </p>
        <p className="ant-upload-hint">
          Поддерживаются форматы JPEG, PNG, GIF, WebP. Максимальный размер — {" "}
          {maxSize} МБ.
        </p>
      </Dragger>
      {uploading && (
        <div style={{ marginTop: 12 }}>
          <Spin tip="Обработка изображения..." />
        </div>
      )}
      {value && (
        <Card
          size="small"
          style={{ marginTop: 16, maxWidth: 320 }}
          cover={
            <img
              src={value}
              alt={altText}
              style={{
                maxHeight: 200,
                objectFit: "contain",
              }}
            />
          }
          actions={[
            <Button
              type="link"
              danger
              onClick={handleRemoveClick}
              disabled={disabled}
              key="remove"
            >
              Удалить
            </Button>,
          ]}
        />
      )}
    </div>
  );
};
